import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import { supabase } from '../services/supabase';
import type { Usuario, UsuarioCreatePayload, UsuarioUpdatePayload } from '../types/usuario';

/**
 * Hooks de gestión de usuarios del sistema.
 *
 * Todas las operaciones de escritura van via api/ (axios) porque requieren
 * service_role para tocar auth.users — ver decisión #014.
 *
 * La lista de empresas activas (para el selector del Dialog) sí va directo
 * a Supabase porque es una lectura CRUD común protegida por RLS.
 */

const QK = {
  all: ['usuarios'] as const,
  empresasActivas: ['empresas', 'activas-simple'] as const,
};

// ── LIST ─────────────────────────────────────────────────────────────────────

export function useUsuarios() {
  return useQuery({
    queryKey: QK.all,
    queryFn: async () => {
      const { data } = await api.get<{ data: Usuario[] }>('/usuarios');
      return data.data;
    },
  });
}

// ── CREATE ────────────────────────────────────────────────────────────────────

export function useCreateUsuario() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: UsuarioCreatePayload) => {
      const { data } = await api.post<{ data: Usuario }>('/usuarios', payload);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.all }),
  });
}

// ── UPDATE ────────────────────────────────────────────────────────────────────

export function useUpdateUsuario() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: UsuarioUpdatePayload }) => {
      const { data } = await api.patch<{ data: Usuario }>(`/usuarios/${id}`, payload);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.all }),
  });
}

// ── DESACTIVAR ────────────────────────────────────────────────────────────────

export function useDesactivarUsuario() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post<{ data: Usuario }>(`/usuarios/${id}/desactivar`);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.all }),
  });
}

// ── ACTIVAR ───────────────────────────────────────────────────────────────────

export function useActivarUsuario() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post<{ data: Usuario }>(`/usuarios/${id}/activar`);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.all }),
  });
}

// ── RESETEAR PASSWORD ─────────────────────────────────────────────────────────

export function useResetPasswordUsuario() {
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post<{ ok: boolean; message: string }>(
        `/usuarios/${id}/resetear-password`
      );
      return data;
    },
  });
}

// ── EMPRESAS ACTIVAS (para el selector del Dialog) ───────────────────────────
// Lectura directa a Supabase (CRUD común — ver decisión #014).

export interface EmpresaSimple {
  id: string;
  razon_social: string;
  cuit: string;
}

export function useEmpresasActivas() {
  return useQuery({
    queryKey: QK.empresasActivas,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('empresas')
        .select('id, razon_social, cuit')
        .eq('estado', 'activa')
        .order('razon_social', { ascending: true });
      if (error) throw error;
      return (data ?? []) as EmpresaSimple[];
    },
    staleTime: 60_000,
  });
}
